"use client"

import { useEffect, useMemo, useState } from "react"
import { z } from "zod"

import { pro } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Badge,
	Button,
	Form,
	FormInput,
	FormSelect,
	IntegrationTemplate,
	Loader,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"

import type { RouterOutputs } from "@/lib/api"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useAutoSave } from "@/lib/use-auto-save"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { IntegrationLayout } from "./integration-layout"

export function CursorIntegrationPage() {
	const workspaceQuery = api.workspace.info.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	return (
		<>
			{workspaceQuery.data ? (
				<CursorForm data={{ workspace: workspaceQuery.data }} />
			) : (
				<div className="flex justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

interface CursorFormProps {
	data: {
		workspace: RouterOutputs["workspace"]["info"]
	}
}

const connectSchema = z.object({
	token: z.string().min(1),
})

const configSchema = z.object({
	repository: z.string(),
	model: z.string().min(1),
	recordingMode: z.enum(["NONE", "SCREENSHOT", "VIDEO"]),
})

const recordingModeOptions = [
	{ label: "Video", value: "VIDEO" },
	{ label: "Screenshot", value: "SCREENSHOT" },
	{ label: "None", value: "NONE" },
]

function CursorForm({ data }: CursorFormProps) {
	const ctx = api.useUtils()
	const disconnectMutation = api.workspace.disconnectCursor.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})
	const connectMutation = api.workspace.connectCursor.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})
	const updateSettingsMutation = api.workspace.updateCursorSettings.useMutation(
		{
			onSuccess: async () => {
				await ctx.workspace.info.invalidate()
			},
		},
	)
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
	const connected = data.workspace.cursorConnected

	const repoQuery = api.workspace.getCursorRepositories.useQuery(undefined, {
		enabled: connected,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	})
	const modelQuery = api.workspace.getCursorModels.useQuery(undefined, {
		enabled: connected,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	})

	const connectForm = useZodForm({
		schema: connectSchema,
	})

	const configForm = useZodForm({
		schema: configSchema,
		defaultValues: {
			repository: data.workspace.cursorRepository ?? "",
			model: data.workspace.cursorModel ?? "default",
			recordingMode: data.workspace.cursorRecordingMode ?? "VIDEO",
		},
		mode: "onChange",
	})

	const [dialogOpen, setDialogOpen] = useState(false)

	useEffect(() => {
		configForm.reset({
			repository: data.workspace.cursorRepository ?? "",
			model: data.workspace.cursorModel ?? "default",
			recordingMode: data.workspace.cursorRecordingMode ?? "VIDEO",
		})
	}, [
		data.workspace.cursorRepository,
		data.workspace.cursorModel,
		data.workspace.cursorRecordingMode,
		configForm,
	])

	useAutoSave({
		form: configForm,
		action: "autosave cursor settings",
		onSave: (v) => {
			updateSettingsMutation.mutate({
				repository: v.repository || undefined,
				model: v.model === "default" ? null : v.model,
				recordingMode: v.recordingMode,
			})
			analytics.track("update-cursor-settings")
		},
	})

	const savedRepo = data.workspace.cursorRepository
	const savedModel = data.workspace.cursorModel

	const repoOptions = useMemo(() => {
		const fromApi = (repoQuery.data ?? []).map((r) => ({
			label: `${r.owner}/${r.name}`,
			value: `${r.owner}/${r.name}`,
		}))
		if (savedRepo && !fromApi.some((o) => o.value === savedRepo)) {
			return [{ label: savedRepo, value: savedRepo }, ...fromApi]
		}
		return fromApi
	}, [repoQuery.data, savedRepo])

	const modelOptions = useMemo(() => {
		const base = [{ label: "Default", value: "default" }]
		const fromApi = (modelQuery.data ?? []).map((m) => ({
			label: m,
			value: m,
		}))
		if (savedModel && !fromApi.some((o) => o.value === savedModel)) {
			return [...base, { label: savedModel, value: savedModel }, ...fromApi]
		}
		return [...base, ...fromApi]
	}, [modelQuery.data, savedModel])

	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<CursorIcon className="h-8 w-8" />}
				name={
					<div className="flex flex-wrap items-center gap-2 font-medium">
						Cursor integration
						{connected ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : null}
					</div>
				}
				description="Connect Cursor Cloud to automatically resolve support tickets."
				button={
					<div>
						{hasRequiredPlan && connected && (
							<Button
								isLoading={disconnectMutation.isLoading}
								onClick={() => setDialogOpen(true)}
							>
								Disconnect
							</Button>
						)}
					</div>
				}
				instructions={
					!connected ? (
						<Form
							form={connectForm}
							onSubmit={(values: { token: string }) => {
								connectMutation.mutate({
									apiKey: values.token,
								})
								analytics.track("connect-cursor")
							}}
							className="space-y-4"
						>
							<div className="text-sm font-medium">Setup</div>
							<SettingsContainer className="space-y-4 divide-y-0">
								<div className="text-sm leading-relaxed text-secondary">
									Create an API key in your{" "}
									<a
										href="https://cursor.com/dashboard"
										target="_blank"
										rel="noopener noreferrer"
										className="text-accent underline"
									>
										Cursor Dashboard
									</a>{" "}
									under Integrations. Make sure your GitHub account is connected
									in Cursor too, so agents can access your repositories.
								</div>
								<FormInput
									name="token"
									label="API key"
									placeholder="cursor_api_..."
									type="password"
								/>
								<div className="flex justify-end">
									{hasRequiredPlan ? (
										<SubmitButton isLoading={connectMutation.isLoading}>
											Connect
										</SubmitButton>
									) : (
										<UpgradeButton
											required={["pro", "scale", "business", "enterprise"]}
										/>
									)}
								</div>
							</SettingsContainer>
						</Form>
					) : (
						<Form
							form={configForm}
							onSubmit={() => undefined}
							className="space-y-4"
						>
							<div className="text-sm font-medium">Configuration</div>
							<SettingsContainer>
								<SettingItem
									title="Repository"
									description="The GitHub repository agents will work on."
									action={
										<FormSelect
											name="repository"
											label={null}
											placeholder={
												repoQuery.isLoading
													? "Loading repositories..."
													: "Select a repository"
											}
											options={repoOptions}
											searchable
											selectClassName="dark:bg-gray-700/70"
										/>
									}
								/>
								<SettingItem
									title="Model"
									description="The AI model used for coding agent tasks."
									action={
										<FormSelect
											name="model"
											label={null}
											options={modelOptions}
											selectClassName="dark:bg-gray-700/70"
										/>
									}
									className="pt-4"
								/>
								<SettingItem
									title="Recording"
									description="What the agent captures when it finishes a run."
									action={
										<FormSelect
											name="recordingMode"
											label={null}
											options={recordingModeOptions}
											selectClassName="dark:bg-gray-700/70"
										/>
									}
									className="pt-4"
								/>
							</SettingsContainer>
							{repoQuery.isError && (
								<div className="text-xs text-red-500">
									Failed to load repositories. Please try again later.
								</div>
							)}
						</Form>
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Cursor Integration?"
				description="Are you sure you want to disconnect Cursor? Your data will remain saved, but the integration will be disabled."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					analytics.track("disconnect-cursor")
					disconnectMutation.mutate()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
